import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { heroVariant, staggerContainer, itemVariant, viewportOnce } from "@/lib/motion";
import { usePageContent } from "@/hooks/usePageContent";
import { useContentItems } from "@/hooks/useContentItem";

const fallback = {
  label: "Blog",
  headline: "Insights & Ideas",
  subheadline: "Thoughts on growth, marketing, and technology.",
  articles: [] as any[],
};

const Blog = () => {
  const { content } = usePageContent("blog", fallback);
  const { data: blogPosts } = useContentItems("blog");

  const articles = blogPosts && blogPosts.length > 0
    ? blogPosts.map((post) => ({
        title: post.title,
        excerpt: post.excerpt || "",
        category: (post.content as any)?.category || "Blog",
        date: post.published_at
          ? new Date(post.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
          : "",
        slug: post.slug,
        thumbnail: (post.content as any)?.thumbnail_image || null,
      }))
    : content.articles;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <section className="relative pt-28 pb-10 md:pt-36 md:pb-16 lg:pt-44 lg:pb-20">
        <div className="container-wide">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl">
            <motion.span variants={heroVariant} className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">
              {content.label}
            </motion.span>
            <motion.h1 variants={heroVariant} className="font-display text-4xl md:text-5xl font-semibold text-foreground mb-6">
              {content.headline}
            </motion.h1>
            <motion.p variants={heroVariant} className="text-lg text-muted-foreground">
              {content.subheadline}
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-10 md:py-16 border-t border-border">
        <div className="container-wide">
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            {articles.map((article: any) => {
              const card = (
                <>
                  {/* Thumbnail */}
                  {article.thumbnail?.src ? (
                    <div className="aspect-[16/9] overflow-hidden bg-muted/30">
                      <img
                        src={article.thumbnail.src}
                        alt={article.thumbnail.alt || article.title}
                        width={article.thumbnail.width || 640}
                        height={article.thumbnail.height || 360}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-muted/20 flex items-center justify-center">
                      <span className="text-xs text-muted-foreground/50 uppercase tracking-wider">
                        {article.category}
                      </span>
                    </div>
                  )}

                  {/* Text */}
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-[11px] text-primary uppercase tracking-wider font-medium">
                        {article.category}
                      </span>
                      {article.date && (
                        <>
                          <span className="text-border">·</span>
                          <span className="text-[11px] text-muted-foreground">{article.date}</span>
                        </>
                      )}
                    </div>
                    <h3 className="text-base font-medium text-foreground leading-snug mb-2">
                      {article.title}
                    </h3>
                    {article.excerpt && (
                      <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                    )}
                  </div>
                </>
              );

              return (
                <motion.article key={article.title} variants={itemVariant}>
                  {article.slug ? (
                    <Link
                      to={`/blog/${article.slug}`}
                      className="block border border-border hover:border-primary/20 transition-colors overflow-hidden"
                    >
                      {card}
                    </Link>
                  ) : (
                    <div className="border border-border overflow-hidden">
                      {card}
                    </div>
                  )}
                </motion.article>
              );
            })}
          </motion.div>

          {articles.length === 0 && (
            <p className="text-center text-muted-foreground py-16">No articles published yet.</p>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Blog;
